// Aktivitätsübersicht (Agent 7A, R-02 bis R-09): Kachelraster mit Zeitraum, Vergleich, Nutzer und Smart View, Rangliste, gespeicherte
// Berichte (auch „Daily KPI's Simon“, der die im CRM erfassten Custom Activities mitzählt). Die Kacheln sind die Konfiguration des Berichts.
import { useCallback, useMemo, useState } from "react";
import { berichteApi, type KennzahlWert } from "../api/berichte";
import { Laden } from "../components/Bausteine";
import { FehlerBox } from "../components/Fehler";
import { useNutzer } from "../auth/AuthContext";
import { BerichtSeite } from "./BerichtSeite";
import { katalogNachId, useGeladen, useKatalog } from "./daten";
import { darfAlleSehen } from "./FilterLeiste";
import { kennzahlenAus, type Konfig } from "./konfig";
import { Kachel, KachelNeu } from "./Kachel";
import { Rangliste } from "./Rangliste";
import { useBerichtSteuerung } from "./steuerung";
import { useBerichtFilter } from "./zustand";

const MAX_KACHELN = 20;

/** Verlauf je Kachel: bei einem Tag keiner, sonst nach Länge Tag, Woche oder Monat (höchstens gut 60 Punkte). */
export function verlaufsArt(von: string, bis: string): "keine" | "tag" | "woche" | "monat" {
  const tage = Math.round((Date.parse(bis) - Date.parse(von)) / 86400000) + 1;
  if (tage <= 1) return "keine";
  if (tage <= 62) return "tag";
  return tage <= 7 * 60 ? "woche" : "monat";
}

export function BerichteStart() {
  const filter = useBerichtFilter("diese_woche");
  const ich = useNutzer();
  const { katalog, fehler: katalogFehler } = useKatalog();
  const [kacheln, setKacheln] = useState<string[]>([]);
  const zusatz = useMemo<Konfig>(() => ({ kennzahlen: kacheln }), [kacheln]);
  const steuerung = useBerichtSteuerung({ art: "uebersicht", filter, zusatz, onAnwenden: (k) => setKacheln(kennzahlenAus(k)) });

  const nachId = useMemo(() => katalogNachId(katalog), [katalog]);
  const sichtbar = kacheln.filter((id) => nachId.has(id));
  const gruppieren = verlaufsArt(filter.tage.von, filter.tage.bis);
  const { eingabe, eingabeSchluessel } = filter;
  const abfrage = useGeladen(
    () => berichteApi.abfrage({ ...eingabe, kennzahlen: sichtbar, gruppieren }),
    `${eingabeSchluessel}|${sichtbar.join(",")}|${gruppieren}`,
    !!katalog && sichtbar.length > 0 && steuerung.geladen,
  );
  const werte = useMemo(() => new Map<string, KennzahlWert>((abfrage.daten?.data ?? []).map((d) => [d.kennzahl, d])), [abfrage.daten]);
  const verwendet = useMemo(() => new Set(kacheln), [kacheln]);

  const tauschen = useCallback((alt: string, neu: string) => setKacheln((a) => a.map((x) => (x === alt ? neu : x))), []);
  const entfernen = useCallback((id: string) => setKacheln((a) => a.filter((x) => x !== id)), []);
  const hinzu = useCallback((id: string) => setKacheln((a) => (a.includes(id) || a.length >= MAX_KACHELN ? a : [...a, id])), []);

  const exportParam = { ...eingabe, kennzahlen: sichtbar };
  return (
    <BerichtSeite
      art="uebersicht"
      filter={filter}
      steuerung={steuerung}
      fehler={abfrage.fehler ?? katalogFehler}
      onNochmal={abfrage.neuLaden}
      csv={
        sichtbar.length
          ? [
              { titel: "Zahlen als CSV laden", art: "abfrage", parameter: exportParam },
              { titel: "Zahlen je Nutzer als CSV laden", art: "abfrage", parameter: { ...exportParam, gruppieren: "nutzer" } },
              ...(gruppieren !== "keine" ? [{ titel: "Verlauf als CSV laden", art: "abfrage" as const, parameter: { ...exportParam, gruppieren } }] : []),
            ]
          : []
      }
    >
      {!katalog ? (
        katalogFehler ? <FehlerBox fehler={katalogFehler} /> : <Laden />
      ) : (
        <div className="bericht-raster">
          <div className="kachelraster" aria-busy={abfrage.laedt}>
            {sichtbar.map((id) => (
              <Kachel
                key={id}
                kennzahl={nachId.get(id)!}
                wert={werte.get(id)}
                katalog={katalog}
                verwendet={verwendet}
                laedt={abfrage.laedt && !abfrage.daten}
                vergleich={!!filter.werte.vergleich}
                onTauschen={(neu) => tauschen(id, neu)}
                onEntfernen={() => entfernen(id)}
              />
            ))}
            {kacheln.length < MAX_KACHELN ? <KachelNeu katalog={katalog} verwendet={verwendet} onWahl={hinzu} /> : null}
          </div>
          {darfAlleSehen(ich.permissions) ? <Rangliste eingabe={eingabe} schluessel={eingabeSchluessel} katalog={katalog} /> : null}
        </div>
      )}
    </BerichtSeite>
  );
}
